import {FC} from 'react'
import {PrintFlowOutcome, PrintFlowTone} from '../printFlow/outcomeModel'
import {formatConfirmedBySource} from '../printFlow/contentPrintSummary'

const toneClass = (tone: PrintFlowTone) => {
  switch (tone) {
    case 'success':
      return 'bg-light-success border-success'
    case 'info':
      return 'bg-light-primary border-primary'
    case 'warning':
      return 'bg-light-warning border-warning'
    case 'danger':
      return 'bg-light-danger border-danger'
    default:
      return 'bg-light border-gray-300'
  }
}

const toneText = (tone: PrintFlowTone) => {
  switch (tone) {
    case 'success':
      return 'text-success'
    case 'info':
      return 'text-primary'
    case 'warning':
      return 'text-warning'
    case 'danger':
      return 'text-danger'
    default:
      return 'text-gray-800'
  }
}

type Props = {outcome: PrintFlowOutcome}

export const PrintFlowOutcomeBanner: FC<Props> = ({outcome}) => (
  <div className={`card card-flush border mb-6 ${toneClass(outcome.tone)}`}>
    <div className='card-body py-6'>
      <div className={`fw-bolder fs-3 mb-1 ${toneText(outcome.tone)}`}>{outcome.title}</div>
      <div className='fw-semibold text-gray-800 fs-6 mb-2'>{outcome.subtitle}</div>
      {outcome.detail && <div className='text-gray-700 fs-6 mb-4'>{outcome.detail}</div>}

      <div className='d-flex flex-wrap gap-4 mb-4'>
        <div>
          <div className='text-muted fs-8 fw-bold'>Printable codes</div>
          <div className='fw-bold fs-4 text-gray-800'>{outcome.stats.printableCodes}</div>
        </div>
        <div>
          <div className='text-muted fs-8 fw-bold'>Printers targeted</div>
          <div className='fw-bold fs-4 text-gray-800'>{outcome.stats.printersTargeted}</div>
        </div>
        <div>
          <div className='text-muted fs-8 fw-bold'>Cache confirmations</div>
          <div className='fw-bold fs-4 text-gray-800'>
            {outcome.stats.cacheConfirmations}/{outcome.stats.cacheExpected} devices
          </div>
          {outcome.stats.prefetchDeliveryAttempts > outcome.stats.cacheExpected && (
            <div className='text-muted fs-8'>
              {outcome.stats.prefetchDeliveryAttempts} delivery attempts
            </div>
          )}
        </div>
        <div>
          <div className='text-muted fs-8 fw-bold'>Physical print jobs</div>
          <div className='fw-bold fs-4 text-gray-800'>{outcome.stats.printJobs}</div>
          {outcome.stats.printAttempts !== outcome.stats.printJobs && (
            <div className='text-muted fs-8'>{outcome.stats.printAttempts} attempts</div>
          )}
        </div>
        <div>
          <div className='text-muted fs-8 fw-bold'>Auto requested</div>
          <div className='fw-bold fs-4 text-gray-800'>{outcome.stats.autoRequestedCopies}</div>
        </div>
        <div>
          <div className='text-muted fs-8 fw-bold'>Confirmed copies</div>
          <div className='fw-bold fs-4 text-gray-800'>{outcome.stats.confirmedCopiesTotal}</div>
        </div>
        {outcome.stats.confirmedCopiesTotal > 0 && (
          <div>
            <div className='text-muted fs-8 fw-bold'>By source</div>
            <div className='fw-bold fs-4 text-gray-800'>
              {formatConfirmedBySource(outcome.stats.confirmedBySource) || '—'}
            </div>
          </div>
        )}
      </div>

      {outcome.attention.length > 0 && (
        <div className='border border-dashed border-gray-300 rounded p-4 bg-white'>
          <div className='fw-bold text-gray-800 mb-2'>Attention</div>
          {outcome.attention.map((item) => (
            <div key={item} className='text-gray-700 fs-6 mb-1'>
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
)
